import { Injectable } from '@angular/core';
import { Column } from '@miq/catalog';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { RecordsSearchService } from '@records/records-search/shared/records-search.service';
import { EntityType } from '@shared/models/entity-enum';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import {
  SetActiveEntity,
  SetFavoritesAction,
  ToggleBeastMode,
  ToggleFavoriteAction,
  ToggleShowFavorites,
  ToggleCollapseAll,
  ToggleExpansion,
  ExpandSections,
  RegisterSections,
} from './form-view-state.actions';
import { FavoriteOptions, FormStateModel, FormViewStateModel } from './form-view-state.model';

const defaultFormState = { beastMode: false, showFavorites: false, collapseAll: true };
const FORM_STATE = 'FormState';

/**
 * Saves FORM_STATE to localStorage
 */
const saveFormStateToLocal = (state: FormViewStateModel) => localStorage.setItem(FORM_STATE, JSON.stringify(state));

/**
 * Before return initial object we need to ensure ALL entities has their values
 * or create a default one (this is to prevent users to reload/refresh session
 * local storage work)
 */
const ensureAllEntitiesAtLeastDefaultOptions = ({ viewStatus = {}, ...state }) => {
  viewStatus = Object.values(EntityType).reduce(
    // keep state state else provide defaultFormState
    (acc, entity) => ({
      ...acc,
      [entity]: viewStatus[entity] || defaultFormState,
    }),
    {}
  );
  return state.expandedSections ? { ...state, viewStatus } : { ...state, viewStatus, expandedSections: {} };
};

/**
 * Loads state from localStorage
 */
const loadFormState = () => {
  if (!localStorage.getItem(FORM_STATE)) {
    const allEntitiesDefaults = Object.values(EntityType).reduce(
      (acc, entity) => ({ ...acc, [entity]: defaultFormState }),
      {}
    );
    saveFormStateToLocal({ viewStatus: allEntitiesDefaults, favorites: [], expandedSections: {} });
  }
  const localStorageState = localStorage.getItem(FORM_STATE);
  return ensureAllEntitiesAtLeastDefaultOptions(JSON.parse(localStorageState));
};

@State<FormViewStateModel>({
  name: FORM_STATE,
  defaults: loadFormState(),
})
@Injectable()
export class FormViewState {
  /**
   * Selects active state and return the beastMode and showFavorite state per entity
   * @param state {FormViewStateModel}
   * @returns state {FormStateModel}
   */
  @Selector()
  static activeState(state: FormViewStateModel) {
    return state.viewStatus[state.activeEntity];
  }

  /**
   * Selects favorites favorites map
   * @param state {FormViewStateModel}
   * @returns favorites {FavoritesMap}
   */
  @Selector()
  static favorites(state: FormViewStateModel) {
    return state.favorites;
  }

  @Selector()
  static collapseAll(state: FormViewStateModel) {
    return state.viewStatus[state.activeEntity].collapseAll;
  }

  @Selector()
  static toggleExpansion(state: FormViewStateModel) {
    return (sectionId: number) => {
      if (state.expandedSections && state.expandedSections.hasOwnProperty(sectionId)) {
        return state.expandedSections[sectionId];
      }
      return true;
    };
  }

  /**
   * Builds a FavoriteOptions object from current state
   * @param state
   * @returns {FavoriteOptions}
   */
  @Selector()
  static favoriteOptions(state: FormViewStateModel): FavoriteOptions {
    const { showFavorites, beastMode } = state.viewStatus[state.activeEntity];
    const showFavoritesOnly = showFavorites && !beastMode;
    return {
      activeEntity: state.activeEntity,
      showFavorites: showFavoritesOnly,
      favorites: state.favorites,
      isCreateMode: state.isCreateMode,
    };
  }

  constructor(
    private userInfoResolver: UserInfoResolver,
    private recordService: RecordsSearchService
  ) {}

  /**
   * Toggles showFavorites value
   * @param ctx
   * @param action
   */
  @Action(ToggleShowFavorites)
  toggleShowFavorites(ctx: StateContext<FormViewStateModel>, action: ToggleShowFavorites) {
    const state = ctx.getState();
    // get state by entity
    const entityState = state.viewStatus[action.entity];
    // update entity state
    const nextEntityState = { ...entityState, showFavorites: action.showFavorites };
    // merge to FormViewState
    const nextState = this.makeNextState(state, action.entity, nextEntityState);
    this.publishState(ctx, nextState);
  }

  /**
   * Toggles beastMode value
   * @param ctx
   * @param action
   */
  @Action(ToggleBeastMode)
  toggleBeastMode(ctx: StateContext<FormViewStateModel>, action: ToggleBeastMode) {
    const state = ctx.getState();
    // get state by entity
    const entityState = state.viewStatus[action.entity];
    // update entity state
    const nextEntityState = { ...entityState, beastMode: !entityState.beastMode };
    // merge to FormViewState
    const nextState = this.makeNextState(state, action.entity, nextEntityState);
    this.publishState(ctx, nextState);
  }

  /**
   * Toggles collapse All value
   * @param ctx
   * @param action
   */
  @Action(ToggleCollapseAll)
  toggleCollapseAll(ctx: StateContext<FormViewStateModel>, action: ToggleCollapseAll) {
    const state = ctx.getState();
    // get state by entity
    const entityState = state.viewStatus[action.entity];
    const expandedState = state.expandedSections;
    action.sectionIds.forEach(sectionId => {
      expandedState[sectionId] = !entityState.collapseAll;
    });
    // update entity state
    const nextEntityState = { ...entityState, collapseAll: !entityState.collapseAll };
    // merge to FormViewState
    const nextState = this.makeNextState({ ...state, expandedSections: expandedState }, action.entity, nextEntityState);
    this.publishState(ctx, nextState);
  }

  /**
   * Toggles expansion value
   * @param ctx
   * @param action
   */
  @Action(ToggleExpansion)
  toggleExpansion(ctx: StateContext<FormViewStateModel>, action: ToggleExpansion) {
    const state = ctx.getState();
    // get state by entity
    const entityState = state.expandedSections;
    entityState[action.sectionId] = !entityState[action.sectionId];
    // merge to FormViewState
    const nextState = { ...state, expandedSections: entityState };
    this.publishState(ctx, nextState);
  }

  /**
   * Expand specific sections
   * @param ctx
   * @param action
   */
  @Action(ExpandSections)
  ExpandSections(ctx: StateContext<FormViewStateModel>, action: ExpandSections) {
    const state = ctx.getState();
    // get state by entity
    const entityState = state.expandedSections;

    action.sectionIds.forEach(sectionId => {
      // Validate if the section is already expanded
      if (!entityState[sectionId]) {
        entityState[sectionId] = true;
      }
    });

    // merge to FormViewState
    const nextState = { ...state, expandedSections: entityState };
    this.publishState(ctx, nextState);
  }

  /**
   * Toggles expansion value
   * @param ctx
   * @param action
   */
  @Action(RegisterSections)
  registerSections(ctx: StateContext<FormViewStateModel>, action: RegisterSections) {
    const state = ctx.getState();
    // get state by entity
    const entityState = state.expandedSections;
    // update state by entity
    if (!entityState.hasOwnProperty(action.sectionId)) {
      entityState[action.sectionId] = true;
    }
    // merge to FormViewState
    const nextState = { ...state, expandedSections: entityState };
    this.publishState(ctx, nextState);
  }

  /**
   * Set activeEntity
   * @param ctx
   * @param action
   */
  @Action(SetActiveEntity)
  setActiveEntity(ctx: StateContext<FormViewStateModel>, action: SetActiveEntity) {
    const state = ctx.getState();
    const nextState = { ...state, activeEntity: action.entity, isCreateMode: action.isCreateMode };
    this.publishState(ctx, nextState);
  }

  /**
   * Updates the set of favorites and transforms from array to FavoritesMap and updates the state
   * usually should be used to initialize or reset teh set of favorite columns
   */
  @Action(SetFavoritesAction)
  setFavoriteColumns(ctx: StateContext<FormViewStateModel>, action: SetFavoritesAction) {
    const state = ctx.getState();
    const favorites = action.favorites.reduce((acc, columnId) => ({ ...acc, [columnId]: true }), {});
    let nextState: FormViewStateModel = { ...ctx.getState(), favorites };

    // display all if no more favorites and showFavorite is enabled
    if (action.favorites.length === 0 && state.viewStatus[state.activeEntity].showFavorites) {
      const currentEntityState = state.viewStatus[state.activeEntity];
      nextState = this.makeNextState(nextState, state.activeEntity, {
        ...currentEntityState,
        showFavorites: false,
      });
    }

    this.publishState(ctx, nextState);
  }

  /**
   * Toggles (add/remove) the column from favorites
   * @param ctx {FormViewStateModel}
   * @param action {ToggleFavoriteAction}
   */
  @Action(ToggleFavoriteAction)
  toggleFavorite(ctx: StateContext<FormViewStateModel>, action: ToggleFavoriteAction) {
    const userId = this.userInfoResolver.resolve().id;
    const state = ctx.getState();
    const currentFavs = Object.keys(state.favorites).map(Number);
    // payload
    const favorites = !action.column.pinned
      ? this.makeAddFavoritesPayload(state, action.column)
      : this.makeDeleteFromFavoritePayload(state, action.column);
    // optimistic update
    this.setFavoriteColumns(ctx, new SetFavoritesAction(favorites));
    // update
    this.recordService
      .updateFavoriteColumns(userId, state.activeEntity, favorites)
      .subscribe({ error: () => this.setFavoriteColumns(ctx, new SetFavoritesAction(currentFavs)) });
  }

  private makeAddFavoritesPayload(state: FormViewStateModel, column: Column) {
    const currentFavorites = Object.keys(state.favorites).map(Number);
    return !state.favorites[column.columnId] ? [...currentFavorites, column.columnId] : currentFavorites;
  }

  private makeDeleteFromFavoritePayload(state: FormViewStateModel, column: Column) {
    return Object.keys(state.favorites)
      .filter(favorite => Number(favorite) !== column.columnId)
      .map(Number);
  }

  /**
   * Saves to local storage and broadcast state to store
   * @param ctx {StateContext<FormViewStateModel>}
   * @param state {FormViewStateModel}
   */
  private publishState(ctx: StateContext<FormViewStateModel>, state: FormViewStateModel) {
    saveFormStateToLocal(state);
    ctx.setState(state);
  }

  /**
   * Updates a by entity state form and merges with full FormViewState object
   * @param state {FormViewStateModel}
   * @param entity {EntityType}
   * @param nextEntityState {FormStateModel}
   */
  private makeNextState(
    state: FormViewStateModel,
    entity: EntityType,
    nextEntityState: FormStateModel
  ): FormViewStateModel {
    const { viewStatus } = state;
    return { ...state, viewStatus: { ...viewStatus, [entity]: nextEntityState } };
  }
}
